import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ValidationPage(): Node {
  return html`
    <div>
      <h1>Validation Rules</h1>
      <p>onefold ships with 8 built-in validation rules. Combine them per field or write custom validators.</p>

      <h2>Built-in Rules</h2>
      <table>
        <tr><th>Rule</th><th>Import</th><th>Description</th></tr>
        <tr><td><code>required()</code></td><td><code>required</code></td><td>Value must be non-empty (trims whitespace).</td></tr>
        <tr><td><code>email()</code></td><td><code>email</code></td><td>Must match a valid email pattern.</td></tr>
        <tr><td><code>minLength(n)</code></td><td><code>minLength</code></td><td>String length must be at least n.</td></tr>
        <tr><td><code>maxLength(n)</code></td><td><code>maxLength</code></td><td>String length must be at most n.</td></tr>
        <tr><td><code>pattern(re)</code></td><td><code>pattern</code></td><td>Must match the given RegExp.</td></tr>
        <tr><td><code>min(n)</code></td><td><code>min</code></td><td>Numeric value must be at least n.</td></tr>
        <tr><td><code>max(n)</code></td><td><code>max</code></td><td>Numeric value must be at most n.</td></tr>
        <tr><td><code>custom(fn)</code></td><td><code>custom</code></td><td>Custom validation function.</td></tr>
      </table>

      <h2>Usage</h2>
      ${CodeBlock(`import { createForm, required, email, minLength, maxLength, min, max, pattern, custom } from 'onefold/form';

const form = createForm({
  fields: {
    username: {
      initial: '',
      rules: [required(), minLength(3), maxLength(20)],
    },
    email: {
      initial: '',
      rules: [required(), email()],
    },
    age: {
      initial: 18,
      rules: [min(13), max(120)],
    },
    phone: {
      initial: '',
      rules: [pattern(/^\\+?[\\d\\s-]{10,}$/)],
    },
    password: {
      initial: '',
      rules: [
        required(),
        minLength(8),
        custom((value) =>
          /[A-Z]/.test(value) ? null : 'Must contain an uppercase letter'
        ),
        custom((value) =>
          /[0-9]/.test(value) ? null : 'Must contain a number'
        ),
      ],
    },
  },
  onSubmit: (values) => console.log(values),
});`)}

      <h2>Custom Validators</h2>
      <p>The <code>custom()</code> rule takes a function that returns <code>null</code> for valid or an error string:</p>
      ${CodeBlock(`// Synchronous custom validator
custom((value) => {
  if (value.includes(' ')) return 'No spaces allowed';
  return null;
})

// Confirm password match
const form = createForm({
  fields: {
    password: { initial: '', rules: [required(), minLength(8)] },
    confirm: {
      initial: '',
      rules: [
        required(),
        custom((value) =>
          value === form.field('password').value()
            ? null
            : 'Passwords do not match'
        ),
      ],
    },
  },
  onSubmit: (values) => { /* ... */ },
});`)}

      ${Callout('Rules are evaluated in order. The first failing rule produces the .error() value. All failures appear in .errors().')}

      <h2>Custom Error Messages</h2>
      <p>Each built-in rule accepts an optional message parameter:</p>
      ${CodeBlock(`rules: [
  required('Please enter your name'),
  minLength(3, 'Name must be at least 3 characters'),
  email('Please enter a valid email address'),
]`)}

      <h2>Try It</h2>
      <p>All built-in rules in action. Interact with each field to see validation errors appear:</p>

      ${Playground(`import { html, mount } from 'onefold';
import { createForm, required, email, minLength, maxLength, min, max, pattern, custom } from 'onefold/form';

function App() {
  const form = createForm({
    username: { initial: '', rules: [
      required('Username is required'),
      minLength(3, 'At least 3 characters'),
      maxLength(20, 'Max 20 characters'),
      pattern(/^[a-zA-Z0-9_]+$/, 'Only letters, numbers, underscore'),
    ]},
    email: { initial: '', rules: [
      required('Email is required'),
      email('Invalid email format'),
    ]},
    age: { initial: 0, rules: [
      min(13, 'Must be at least 13'),
      max(120, 'Must be under 120'),
    ]},
    website: { initial: '', rules: [
      custom((v) => !v || v.startsWith('https://'), 'Must start with https://'),
    ]},
  });

  return html\`
    <div>
      <h3>All Validation Rules</h3>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Username (required, 3-20 chars, alphanumeric)</label>
        <input oninput=\${form.fields.username.handle} placeholder="johndoe" style="width:100%" />
        \${() => form.fields.username.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:3px">\${form.fields.username.error()}</p>\` : null}
      </div>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Email (required, email format)</label>
        <input type="email" oninput=\${form.fields.email.handle} placeholder="you@example.com" style="width:100%" />
        \${() => form.fields.email.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:3px">\${form.fields.email.error()}</p>\` : null}
      </div>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Age (min: 13, max: 120)</label>
        <input type="number" oninput=\${form.fields.age.handle} placeholder="25" style="width:100%" />
        \${() => form.fields.age.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:3px">\${form.fields.age.error()}</p>\` : null}
      </div>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Website (custom: must start with https://)</label>
        <input oninput=\${form.fields.website.handle} placeholder="https://example.com" style="width:100%" />
        \${() => form.fields.website.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:3px">\${form.fields.website.error()}</p>\` : null}
      </div>

      <div style="display:flex;gap:8px;align-items:center;margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb">
        <button onclick=\${() => form.submit((v) => alert('Submitted: ' + JSON.stringify(v)))}>Submit</button>
        <button onclick=\${() => form.reset()}>Reset All</button>
        <span style="font-size:12px;color:#666;margin-left:auto">\${() => form.valid() ? '✓ All valid' : '✗ Has errors'}</span>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'All Validation Rules — required, email, minLength, maxLength, min, max, pattern, custom')}
    </div>
  `;
}
